import { BarChart, BarChartProps } from '@mui/x-charts/BarChart';
import { CashFlowTrendPoint, formatMoney } from '@dill/dill-shared';
import { COLORS } from '../../../../../utils/colors';

interface CashFlowTrendChartProps {
  data: CashFlowTrendPoint[];
}

const getResponsiveConfig = (): Partial<BarChartProps> => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return {
    height: isMobile ? 350 : 400,
    margin: {
      left: isMobile ? 60 : 80,
      right: isMobile ? 15 : 20,
      top: 20,
      bottom: isMobile ? 80 : 60,
    },
    hideLegend: false,
  };
};

function CashFlowTrendChart({ data }: CashFlowTrendChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        No cash flow data available
      </div>
    );
  }

  const dataSet = data.map((point) => ({
    month: point.month.split('-')[1],
    invoiced: point.invoiced,
    received: point.received,
  }));

  return (
    <div className="w-full">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">Cash Flow Trend</h3>
          <p className="text-sm text-gray-500">Monthly invoiced vs paid amounts</p>
        </div>
      </div>
      <BarChart
        borderRadius={8}
        dataset={dataSet}
        xAxis={[
          {
            dataKey: 'month',
            tickLabelStyle: {
              fontSize: 12,
              fill: COLORS.TEXT_SECONDARY,
              fontFamily: 'Inter',
              fontWeight: 'bold',
            },
          },
        ]}
        series={[
          {
            dataKey: 'invoiced',
            label: 'Invoiced',
            color: COLORS.PRIMARY_500,
            valueFormatter: (value: number | null) => {
              return `${formatMoney(value || 0, 2)}`;
            },
          },
          {
            dataKey: 'received',
            label: 'Paid',
            color: COLORS.SUCCESS_500,
            valueFormatter: (value: number | null) => {
              return `${formatMoney(value || 0, 2)}`;
            },
          },
        ]}
        yAxis={[
          {
            width: 80,
            tickLabelStyle: {
              fontSize: 11,
              fill: COLORS.TEXT_SECONDARY,
              fontFamily: 'Inter',
              fontWeight: 'bold',
            },
            domainLimit: (min, max) => {
              return { min, max: Math.floor(max + (max * 10) / 100) };
            },
            valueFormatter: (value: number) => {
              return formatMoney(value, 0);
            },
          },
        ]}
        grid={{ horizontal: true }}
        {...getResponsiveConfig()}
      />
    </div>
  );
}

export default CashFlowTrendChart;
